import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { NgForm } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { getUserInfo } from '@util/auth/helpers';
import { UsersService } from '@services/users.service';
import {
  LexgosignPaymentConfig,
  LexgosignPaymentQuote,
  LexgosignService
} from '@services/lexgosign.service';
import { RequirementService } from 'src/app/services/requirement.service';
import { AttachmentsService } from 'src/app/services/attachments.service';
import { LexgoPlacementMarker } from '../lexgosign-placement/lexgosign-placement.component';
import { LexgosignFolderPickerComponent } from '../lexgosign-folder-picker/lexgosign-folder-picker.component';
import {
  LEXGO_PHONE_COUNTRIES,
  composePhone,
  parsePhone
} from './lexgosign-phone-countries';

export type LexgoElectronicSignatureType = 'SIMPLE' | 'ADVANCED';
export type LexgoPaymentMethod = 'card' | 'manual';
export type LexgoWizardStep = 1 | 2 | 3 | 4;
export type LexgoPlacementMode = 'extraPage' | 'custom';

@Component({
  selector: 'app-lexgosign-send',
  templateUrl: './lexgosign-send.component.html',
  styleUrls: ['./lexgosign-send.component.scss'],
  standalone: false
})
export class LexgosignSendComponent implements OnInit, OnDestroy {
  /** Si true (o sin requirementId), flujo Firma Contrato sin requerimiento */
  @Input() standalone = false;
  @Input() requirementId: string | number | null = null;
  /** PDF ya generado (p. ej. desde Templates) para saltar la carga. */
  @Input() preloadedFile: File | null = null;
  @Input() initialStep: LexgoWizardStep | null = null;
  @Output() sent = new EventEmitter<boolean>();

  readonly steps = [
    { id: 1 as LexgoWizardStep, label: 'Subir documentos' },
    { id: 2 as LexgoWizardStep, label: 'Añadir firmantes' },
    { id: 3 as LexgoWizardStep, label: 'Posición de firmas' },
    { id: 4 as LexgoWizardStep, label: 'Revisar y enviar' }
  ];

  get isStandalone(): boolean {
    return this.standalone || this.requirementId == null || this.requirementId === '';
  }

  step: LexgoWizardStep = 1;
  signingparties: {
    name: string;
    lastname?: string;
    email: string;
    identification?: string;
    phone?: string;
    phoneCountry?: string;
    phoneLocal?: string;
    repId?: string;
    representsCompany?: boolean;
  }[] = [];
  signerDraft = {
    name: '',
    lastname: '',
    email: '',
    identification: '',
    phoneCountry: 'CL',
    phoneLocal: '',
    repId: ''
  };
  editingIndex: number | null = null;
  readonly phoneCountries = LEXGO_PHONE_COUNTRIES;
  attachments: any[] = [];
  selectedAttachmentId: number | null = null;
  uploadedFile: File | null = null;
  createdBy = '';
  loading = false;
  loadingFiles = false;
  loadingPdf = false;
  errorMessage = '';
  successMessage = '';
  validEmailFormat = false;

  /** SIMPLE = Lexgo gratis; ADVANCED = FirmAki (FEA, de pago). */
  electronicSignatureType: LexgoElectronicSignatureType = 'SIMPLE';
  paymentMethod: LexgoPaymentMethod = 'card';
  couponCode = '';
  appliedCouponCode = '';
  couponMessage = '';
  paymentConfig: LexgosignPaymentConfig | null = null;
  paymentQuote: LexgosignPaymentQuote | null = null;
  paymentLoading = false;
  paymentIntentId = '';
  /** Por qué no hay cotización, si no hay. Bloquea el envío. */
  paymentQuoteError: string | null = null;
  /** El cargo de este modal ya pasó: reintentar NO debe volver a cobrar. */
  paymentConfirmed = false;
  clientSecret = '';
  private stripeInstance: any = null;
  private cardElement: any = null;
  private stripeScriptLoading: Promise<any> | null = null;

  sequential = false;
  /** Preferencia UI: true = posicionar en PDF; false = hoja adicional (default Lexgo). */
  preferCustomPlacement = false;
  placementMode: LexgoPlacementMode = 'extraPage';
  /** SIMPLE = solo dibujo; FULL = con QR y pie. En hoja adicional siempre FULL. */
  signatureStyle: 'SIMPLE' | 'FULL' = 'FULL';
  language: 'ES' | 'EN' = 'ES';
  signerMessage = '';
  readonly signerMessageMax = 500;
  archivePath = 'Contratos';
  saveEvidenceSheet = true;
  advancedTab: 'flow' | 'position' | 'org' = 'flow';
  placements: LexgoPlacementMarker[] = [];
  /** Buffer original del PDF (nunca se pasa a pdf.js; se clona al previsualizar). */
  pdfSource: ArrayBuffer | null = null;
  /** Copia para el visor; pdf.js la puede invalidar al cargar. */
  pdfData: ArrayBuffer | null = null;

  users: { id: number; name: string; email: string; label: string }[] = [];
  userSearchQuery = '';
  userSearchMatches: { id: number; name: string; email: string; label: string }[] = [];
  loadingUsers = false;
  showManualSignerForm = false;

  constructor(
    public activeModal: NgbActiveModal,
    private lexgosignService: LexgosignService,
    private usersService: UsersService,
    private requirements: RequirementService,
    private attachmentsService: AttachmentsService,
    private modalService: NgbModal,
    private http: HttpClient
  ) {}

  ngOnInit() {
    const userInfo = getUserInfo();
    if (userInfo?.data?.id) {
      this.usersService.getUser(userInfo.data.id).subscribe((response: any) => {
        this.createdBy = response?.result?.name || '';
      });
    }
    this.loadUsers();
    this.loadPaymentConfig();
    if (this.preloadedFile) {
      this.uploadedFile = this.preloadedFile;
      this.standalone = true;
      if (this.initialStep) {
        this.step = this.initialStep;
      }
    }
    if (!this.isStandalone) {
      this.loadAttachments();
    }
  }

  loadUsers() {
    this.loadingUsers = true;
    this.usersService.getUsers().subscribe({
      next: (response: any) => {
        const list = response?.result || response || [];
        this.users = (Array.isArray(list) ? list : [])
          .filter((u: any) => u && u.email)
          .map((u: any) => ({
            id: Number(u.id),
            name: String(u.name || '').trim(),
            email: String(u.email || '').trim(),
            label: `${u.name || ''} — ${u.email || ''}`.trim()
          }))
          .sort((a, b) => a.label.localeCompare(b.label, 'es'));
        this.loadingUsers = false;
      },
      error: () => {
        this.users = [];
        this.loadingUsers = false;
      }
    });
  }

  private splitPersonName(full: string): { name: string; lastname: string } {
    const parts = String(full || '')
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    if (!parts.length) return { name: '', lastname: '' };
    if (parts.length === 1) return { name: parts[0], lastname: '' };
    return { name: parts[0], lastname: parts.slice(1).join(' ') };
  }

  applyUser(user: { id: number; name: string; email: string }) {
    if (!user) return;
    const split = this.splitPersonName(user.name);
    this.signerDraft = {
      ...this.signerDraft,
      name: split.name,
      lastname: split.lastname,
      email: user.email || ''
    };
    this.userSearchQuery = user.email || '';
    this.userSearchMatches = [];
    this.showManualSignerForm = true;
    this.validEmailFormat = false;
    this.errorMessage = '';
  }

  searchAndFillUser() {
    const q = (this.userSearchQuery || '').trim().toLowerCase();
    this.userSearchMatches = [];
    if (!q) {
      this.errorMessage = 'Ingrese un email o nombre para buscar.';
      return;
    }
    const matches = this.users.filter(
      (u) =>
        u.email.toLowerCase() === q ||
        u.email.toLowerCase().includes(q) ||
        u.name.toLowerCase().includes(q)
    );
    if (!matches.length) {
      this.errorMessage = 'No se encontró un usuario con ese email o nombre. Puede ingresar los datos manualmente.';
      this.showManualSignerForm = true;
      return;
    }
    this.errorMessage = '';
    if (matches.length === 1) {
      this.applyUser(matches[0]);
      return;
    }
    this.userSearchMatches = matches.slice(0, 12);
  }

  enableManualSignerForm() {
    this.showManualSignerForm = true;
    this.userSearchMatches = [];
  }

  get signatureStyleEffective(): 'SIMPLE' | 'FULL' {
    return this.placementMode === 'extraPage' ? 'FULL' : this.signatureStyle;
  }

  get signerMessageLength(): number {
    return (this.signerMessage || '').length;
  }

  ngOnDestroy() {
    this.destroyStripeCard();
  }

  loadPaymentConfig() {
    this.lexgosignService.getPaymentConfig().subscribe({
      next: (cfg) => {
        this.paymentConfig = cfg;
      },
      error: () => {
        this.paymentConfig = null;
      }
    });
  }

  get isAdvancedSignature(): boolean {
    return this.electronicSignatureType === 'ADVANCED';
  }

  get advancedUnitPriceLabel(): string {
    const price = this.paymentConfig?.advancedUnitPriceUsd ?? 6;
    return `$${price} USD`;
  }

  setElectronicSignatureType(type: LexgoElectronicSignatureType) {
    this.electronicSignatureType = type;
    if (type === 'ADVANCED') {
      this.signatureStyle = 'FULL';
    }
    this.paymentIntentId = '';
    this.paymentConfirmed = false;
    this.clientSecret = '';
  }

  onPaymentMethodChange(method: LexgoPaymentMethod) {
    this.paymentMethod = method;
    this.paymentIntentId = '';
    this.paymentConfirmed = false;
    this.clientSecret = '';
    if (method === 'card' && this.step === 4) {
      setTimeout(() => this.initStripeCard(), 0);
    } else {
      this.destroyStripeCard();
    }
  }

  refreshPaymentQuote() {
    if (!this.isAdvancedSignature) {
      this.paymentQuote = null;
      return;
    }
    this.paymentLoading = true;
    this.couponMessage = '';
    this.paymentQuoteError = null;
    this.lexgosignService.getPaymentQuote(1, this.appliedCouponCode || undefined).subscribe({
      next: (quote) => {
        this.paymentQuote = quote;
        this.paymentLoading = false;
        if (quote.couponInvalid) {
          this.couponMessage = 'Cupón no válido';
        } else if (quote.coupon?.code) {
          this.couponMessage = 'Cupón aplicado';
        }
      },
      error: (e: any) => {
        this.paymentLoading = false;
        this.paymentQuote = null;
        // Sin esto la sección de costos quedaba vacía y el botón habilitado: el
        // usuario pagaba un importe que nunca vio.
        this.paymentQuoteError =
          e?.message || 'No se pudo calcular el costo. Intenta de nuevo.';
      }
    });
  }

  applyCoupon() {
    this.appliedCouponCode = (this.couponCode || '').trim().toUpperCase();
    this.refreshPaymentQuote();
  }

  private loadStripeJs(): Promise<any> {
    const w = window as any;
    if (w.Stripe) {
      return Promise.resolve(w.Stripe);
    }
    if (this.stripeScriptLoading) {
      return this.stripeScriptLoading;
    }
    this.stripeScriptLoading = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://js.stripe.com/v3/';
      script.async = true;
      script.onload = () => resolve((window as any).Stripe);
      script.onerror = () => reject(new Error('No se pudo cargar Stripe.js'));
      document.head.appendChild(script);
    });
    return this.stripeScriptLoading;
  }

  private destroyStripeCard() {
    if (this.cardElement) {
      try {
        this.cardElement.destroy();
      } catch {
        /* ignore */
      }
      this.cardElement = null;
    }
    this.stripeInstance = null;
  }

  async initStripeCard() {
    if (!this.isAdvancedSignature || this.paymentMethod !== 'card') {
      return;
    }
    const cfg = this.paymentConfig;
    if (!cfg || cfg.mock || !cfg.stripePublishableKey) {
      return;
    }
    try {
      const StripeFactory = await this.loadStripeJs();
      this.destroyStripeCard();
      this.stripeInstance = StripeFactory(cfg.stripePublishableKey, { locale: 'es' });
      const elements = this.stripeInstance.elements();
      this.cardElement = elements.create('card', {
        hidePostalCode: true,
        style: {
          base: {
            fontSize: '16px',
            color: '#213552',
            '::placeholder': { color: '#aab7c4' }
          }
        }
      });
      const mountEl = document.getElementById('lexgo-stripe-card');
      if (mountEl) {
        mountEl.innerHTML = '';
        this.cardElement.mount('#lexgo-stripe-card');
      }
    } catch {
      this.errorMessage = 'No se pudo inicializar el formulario de pago.';
    }
  }

  private onEnterReviewStep() {
    if (!this.isAdvancedSignature) {
      return;
    }
    this.refreshPaymentQuote();
    setTimeout(() => this.initStripeCard(), 0);
  }

  private processCardPayment(): Promise<boolean> {
    return new Promise((resolve) => {
      this.lexgosignService.createPaymentIntent(1, this.appliedCouponCode || undefined).subscribe({
        next: async (intent) => {
          if (!intent?.paymentIntentId) {
            this.loading = false;
            this.errorMessage = 'No se pudo iniciar el pago.';
            resolve(false);
            return;
          }
          this.paymentIntentId = intent.paymentIntentId;
          this.clientSecret = intent.clientSecret;
          this.paymentQuote = intent.quote || this.paymentQuote;

          // Solo el backend decide si esto es simulado: `intent.mock` viene de él.
          // Antes se aceptaba también `!stripeConfigured`, así que un
          // /payment/config fallido —o ausente— se leía como modo demo: se saltaba
          // la confirmación de la tarjeta y se mandaba un intent sin confirmar,
          // que el backend rechazaba después con "El pago no fue confirmado".
          if (intent.mock) {
            resolve(true);
            return;
          }
          if (!this.paymentConfig?.stripeConfigured) {
            this.loading = false;
            this.errorMessage =
              'El pago no está disponible en esta cuenta. Contacta a soporte.';
            resolve(false);
            return;
          }

          if (!this.stripeInstance || !this.cardElement) {
            this.loading = false;
            this.errorMessage = 'Complete los datos de la tarjeta.';
            resolve(false);
            return;
          }

          // Sin try/catch, un rechazo de la promesa dejaba `loading` en true y el
          // botón congelado en "Enviando…" sin ningún mensaje.
          let result: any;
          try {
            result = await this.stripeInstance.confirmCardPayment(this.clientSecret, {
              payment_method: { card: this.cardElement }
            });
          } catch (e: any) {
            this.loading = false;
            this.errorMessage =
              e?.message || 'No se pudo confirmar el pago. Revisa los datos de la tarjeta.';
            resolve(false);
            return;
          }
          if (result.error) {
            this.loading = false;
            this.errorMessage = result.error.message || 'Error al procesar el pago.';
            resolve(false);
            return;
          }
          if (result.paymentIntent?.status === 'succeeded') {
            this.paymentIntentId = result.paymentIntent.id;
            resolve(true);
            return;
          }
          this.loading = false;
          this.errorMessage = 'El pago no fue confirmado.';
          resolve(false);
        },
        error: (err) => {
          this.loading = false;
          this.errorMessage =
            err?.error?.message || err?.message || 'Error al crear la intención de pago.';
          resolve(false);
        }
      });
    });
  }

  loadAttachments() {
    if (this.isStandalone || this.requirementId == null) {
      return;
    }
    this.loadingFiles = true;
    this.requirements.getRequirementAllData(this.requirementId).subscribe(
      (resp: any) => {
        const files = resp?.result?.attachments || [];
        this.attachments = files.filter((f: any) => {
          const name = (f.filename || f.name || '').toLowerCase();
          const type = (f.contenttype || f.mimetype || '').toLowerCase();
          return name.endsWith('.pdf') || type.includes('pdf');
        });
        this.loadingFiles = false;
      },
      () => {
        this.loadingFiles = false;
        this.errorMessage = 'No se pudieron cargar los documentos del requerimiento.';
      }
    );
  }

  closeModal() {
    this.activeModal.close('Modal Closed');
  }

  validateEmail(email: string): boolean {
    if (!email) {
      return false;
    }
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  validateEmailFormat(email: string) {
    this.validEmailFormat = email ? !this.validateEmail(email) : false;
  }

  createFields(form: NgForm) {
    if (!form.valid) {
      return;
    }
    const value = this.signerDraft;
    if (!this.validateEmail(value.email)) {
      this.validEmailFormat = true;
      return;
    }
    const party = {
      name: (value.name || '').trim(),
      lastname: (value.lastname || '').trim(),
      email: (value.email || '').trim(),
      identification: (value.identification || '').trim() || undefined,
      phoneCountry: value.phoneCountry || 'CL',
      phoneLocal: (value.phoneLocal || '').trim() || undefined,
      phone: composePhone(value.phoneCountry, value.phoneLocal),
      repId: (value.repId || '').trim() || undefined,
      representsCompany: !!(value.repId || '').trim()
    };
    if (this.editingIndex != null) {
      this.signingparties[this.editingIndex] = party;
    } else {
      this.signingparties.push(party);
      this.placements = [];
    }
    this.resetSignerForm();
  }

  editField(index: number) {
    const party = this.signingparties[index];
    if (!party) return;
    const parsed = parsePhone(party.phone);
    this.signerDraft = {
      name: party.name || '',
      lastname: party.lastname || '',
      email: party.email || '',
      identification: party.identification || '',
      phoneCountry: party.phoneCountry || parsed.iso,
      phoneLocal: party.phoneLocal || parsed.local,
      repId: party.repId || ''
    };
    this.editingIndex = index;
    this.validEmailFormat = false;
    this.errorMessage = '';
  }

  cancelEdit() {
    this.resetSignerForm();
  }

  private resetSignerForm() {
    this.signerDraft = {
      name: '',
      lastname: '',
      email: '',
      identification: '',
      phoneCountry: 'CL',
      phoneLocal: '',
      repId: ''
    };
    this.editingIndex = null;
    this.validEmailFormat = false;
  }

  removeField(index: number) {
    this.signingparties.splice(index, 1);
    this.placements = (this.placements || [])
      .filter((p) => p.recipientIndex !== index)
      .map((p) =>
        p.recipientIndex > index ? { ...p, recipientIndex: p.recipientIndex - 1 } : p
      );
    if (this.editingIndex === index) {
      this.resetSignerForm();
    } else if (this.editingIndex != null && this.editingIndex > index) {
      this.editingIndex -= 1;
    }
  }

  get selectedAttachment() {
    return this.attachments.find((a) => a.id === this.selectedAttachmentId);
  }

  get hasDocument(): boolean {
    if (this.isStandalone) {
      return !!this.uploadedFile;
    }
    return !!(this.selectedAttachmentId || this.uploadedFile);
  }

  get documentLabel(): string {
    if (this.uploadedFile) {
      return this.uploadedFile.name;
    }
    const att = this.selectedAttachment;
    return att?.filename || att?.name || '—';
  }

  /** Nombre del envelope = nombre del PDF (sin extensión). Lexgo lo usa como título. */
  get envelopeTitle(): string {
    const label = (this.documentLabel || '').trim();
    if (!label || label === '—') return '';
    return label.replace(/\.[^/.]+$/, '') || label;
  }

  get allSignersPlaced(): boolean {
    if (!this.signingparties.length) {
      return false;
    }
    return this.signingparties.every((_, i) =>
      (this.placements || []).some((p) => p.recipientIndex === i)
    );
  }

  onExistingSelected() {
    if (this.selectedAttachmentId) {
      this.uploadedFile = null;
      this.clearPdfBuffers();
      this.placements = [];
    }
  }

  retrieveFiles(event: any[]) {
    if (!event || !event.length) {
      this.uploadedFile = null;
      return;
    }
    const file = event[event.length - 1];
    if (!file) {
      return;
    }
    const name = (file.name || '').toLowerCase();
    if (!name.endsWith('.pdf') && file.type !== 'application/pdf') {
      this.errorMessage = 'Solo se permiten archivos PDF.';
      this.uploadedFile = null;
      return;
    }
    this.uploadedFile = file;
    this.selectedAttachmentId = null;
    this.clearPdfBuffers();
    this.placements = [];
  }

  clearUploadedFile() {
    this.uploadedFile = null;
    this.clearPdfBuffers();
    this.placements = [];
  }

  setPlacementMode(mode: LexgoPlacementMode) {
    this.placementMode = mode;
    this.preferCustomPlacement = mode === 'custom';
    if (mode === 'extraPage') {
      this.placements = [];
      this.pdfData = null;
      this.signatureStyle = 'FULL';
    } else {
      if (this.signatureStyle !== 'SIMPLE' && this.signatureStyle !== 'FULL') {
        this.signatureStyle = 'SIMPLE';
      }
      this.ensurePdfLoaded(true);
    }
  }

  onPreferCustomPlacementChange() {
    this.setPlacementMode(this.preferCustomPlacement ? 'custom' : 'extraPage');
  }

  onSignatureStyleChange() {
    if (this.placementMode === 'extraPage') {
      this.signatureStyle = 'FULL';
    }
  }

  openArchiveFolderPicker() {
    const ref = this.modalService.open(LexgosignFolderPickerComponent, {
      size: 'md',
      backdrop: 'static',
      centered: true
    });
    ref.componentInstance.initialPath = this.archivePath || 'Contratos';
    ref.result.then(
      (path: string) => {
        if (path) this.archivePath = path;
      },
      () => undefined
    );
  }

  onPlacementsChange(list: LexgoPlacementMarker[]) {
    this.placements = list || [];
  }

  canGoNext(): boolean {
    this.errorMessage = '';
    if (this.step === 1) {
      if (!this.hasDocument) {
        this.errorMessage = this.isStandalone
          ? 'Suba un documento PDF para firmar.'
          : 'Seleccione un PDF del requerimiento o cargue uno nuevo.';
        return false;
      }
      return true;
    }
    if (this.step === 2) {
      if (!this.signingparties.length) {
        this.errorMessage = 'Agregue al menos un firmante.';
        return false;
      }
      return true;
    }
    if (this.step === 3) {
      if (this.placementMode === 'custom' && !this.allSignersPlaced) {
        this.errorMessage = 'Posicione la firma de cada firmante en el documento.';
        return false;
      }
      return true;
    }
    return true;
  }

  nextStep() {
    if (!this.canGoNext()) {
      return;
    }
    if (this.step === 2 && this.placementMode === 'extraPage') {
      this.step = 4;
      this.errorMessage = '';
      this.onEnterReviewStep();
      return;
    }
    if (this.step < 4) {
      this.step = (this.step + 1) as LexgoWizardStep;
      this.errorMessage = '';
      if (this.step === 3 && this.placementMode === 'custom') {
        this.ensurePdfLoaded();
      }
      if (this.step === 4) {
        this.onEnterReviewStep();
      }
    }
  }

  prevStep() {
    if (this.step === 4 && this.placementMode === 'extraPage') {
      this.step = 2;
      this.errorMessage = '';
      return;
    }
    if (this.step > 1) {
      this.step = (this.step - 1) as LexgoWizardStep;
      this.errorMessage = '';
    }
  }

  goToStep(target: LexgoWizardStep) {
    if (target >= this.step) {
      return;
    }
    this.step = target;
    this.errorMessage = '';
  }

  private clearPdfBuffers() {
    this.pdfSource = null;
    this.pdfData = null;
  }

  private usePdfSource(buf: ArrayBuffer) {
    this.pdfSource = buf;
    this.pdfData = buf.slice(0);
  }

  ensurePdfLoaded(_force = false) {
    if (this.loadingPdf) {
      return;
    }
    if (this.pdfSource) {
      this.pdfData = this.pdfSource.slice(0);
      return;
    }
    this.loadingPdf = true;
    this.errorMessage = '';
    this.pdfData = null;

    if (this.uploadedFile) {
      this.uploadedFile
        .arrayBuffer()
        .then((buf) => {
          this.usePdfSource(buf);
          this.loadingPdf = false;
        })
        .catch(() => {
          this.loadingPdf = false;
          this.errorMessage = 'No se pudo leer el PDF cargado.';
        });
      return;
    }

    const att = this.selectedAttachment;
    if (!att) {
      this.loadingPdf = false;
      this.errorMessage = 'No hay documento seleccionado.';
      return;
    }

    const applyUrl = (url: string) => {
      this.http.get(url, { responseType: 'arraybuffer' }).subscribe({
        next: (buf) => {
          this.usePdfSource(buf);
          this.loadingPdf = false;
        },
        error: () => {
          this.loadingPdf = false;
          this.errorMessage = 'No se pudo descargar el PDF para previsualizar.';
        }
      });
    };

    if (att.url) {
      applyUrl(att.url);
      return;
    }

    this.attachmentsService.getAttachmentDownloadUrl(att.id).subscribe({
      next: (resp: any) => {
        const url = resp?.result || resp;
        if (typeof url === 'string' && url.startsWith('http')) {
          applyUrl(url);
        } else {
          this.loadingPdf = false;
          this.errorMessage = 'No se obtuvo URL del documento.';
        }
      },
      error: () => {
        this.loadingPdf = false;
        this.errorMessage = 'No se pudo obtener el documento.';
      }
    });
  }

  private getFormData(body: { requirementid: any; note: string; file: File }): FormData {
    const formData = new FormData();
    formData.append('file', body.file);
    formData.append('requirementid', String(body.requirementid));
    formData.append('note', body.note || '');
    return formData;
  }

  private handleCreateResponse(resp: any) {
    this.loading = false;
    const body = resp?.result ?? resp;
    if (resp && resp.success === false) {
      this.errorMessage = resp.message || 'Error al crear la firma CLM Sign.';
      return;
    }
    if (body && body.success === false) {
      this.errorMessage = body.message || 'Error al crear la firma CLM Sign.';
      return;
    }
    const result = body?.result ?? body;
    const withLink = (result?.recipients || []).find((r: any) => r.signingUrl);
    this.successMessage = this.isStandalone
      ? 'Firma Contrato enviada correctamente.'
      : withLink
        ? 'Firma lista. Puede firmar desde el sidebar (Firmar en Flow).'
        : 'Firma enviada. El enlace aparecerá en el sidebar cuando esté disponible.';
    this.sent.emit(true);
    setTimeout(() => this.activeModal.close(result || true), 700);
  }

  private handleCreateError(error: any) {
    this.loading = false;
    this.errorMessage =
      error?.error?.message || error?.message || 'Error al crear la firma CLM Sign.';
  }

  private buildCreateOptions(extra: {
    attachmentId?: number;
    attachmentName: string;
    documentBase64?: string;
    documentName?: string;
  }) {
    const useExtraPage = this.placementMode === 'extraPage';
    return {
      requirementId: this.isStandalone ? null : this.requirementId,
      name: this.envelopeTitle || extra.attachmentName || 'documento',
      attachmentName: extra.attachmentName,
      attachmentId: extra.attachmentId,
      documentBase64: extra.documentBase64,
      documentName: extra.documentName || extra.attachmentName,
      recipients: this.signingparties,
      createdBy: this.createdBy,
      sequential: this.sequential,
      language: this.language,
      useExtraPage,
      signatureStyle: this.signatureStyleEffective,
      electronicSignatureType: this.electronicSignatureType,
      paymentMethod: this.isAdvancedSignature ? this.paymentMethod : undefined,
      paymentIntentId:
        this.isAdvancedSignature && this.paymentMethod === 'card'
          ? this.paymentIntentId || undefined
          : undefined,
      couponCode: this.isAdvancedSignature ? this.appliedCouponCode || undefined : undefined,
      documentCount: 1,
      signerMessage: (this.signerMessage || '').trim().slice(0, this.signerMessageMax) || undefined,
      archivePath: this.archivePath || 'Contratos',
      saveEvidence: this.saveEvidenceSheet !== false,
      placements: useExtraPage
        ? undefined
        : (this.placements || []).map((p) => ({
            recipientIndex: p.recipientIndex,
            page: p.page,
            top: p.top,
            left: p.left
          }))
    };
  }

  private createEnvelope(attachmentId: number, attachmentName: string) {
    this.lexgosignService
      .create(this.buildCreateOptions({ attachmentId, attachmentName }))
      .subscribe(
        (resp) => this.handleCreateResponse(resp),
        (error) => this.handleCreateError(error)
      );
  }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = String(reader.result || '');
        const base64 = result.includes(',') ? result.split(',')[1] : result;
        resolve(base64);
      };
      reader.onerror = () => reject(reader.error || new Error('No se pudo leer el archivo'));
      reader.readAsDataURL(file);
    });
  }

  private createEnvelopeStandalone(file: File) {
    this.fileToBase64(file)
      .then((documentBase64) => {
        this.lexgosignService
          .create(
            this.buildCreateOptions({
              attachmentName: file.name || 'documento.pdf',
              documentBase64,
              documentName: file.name || 'documento.pdf'
            })
          )
          .subscribe(
            (resp) => this.handleCreateResponse(resp),
            (error) => this.handleCreateError(error)
          );
      })
      .catch(() => {
        this.loading = false;
        this.errorMessage = 'No se pudo leer el PDF para enviarlo.';
      });
  }

  /**
   * Con Firma Avanzada y tarjeta no se envía sin un importe A LA VISTA.
   *
   * Si la cotización falló, `paymentQuote` es null y antes el botón seguía
   * habilitado: se cobraba un total que el usuario nunca vio. El pago ya
   * confirmado sí puede seguir, porque reintentar no debe volver a cobrar.
   */
  get puedeEnviarConPago(): boolean {
    if (!this.isAdvancedSignature || this.paymentMethod !== 'card') return true;
    if (this.paymentConfirmed) return true;
    return !!this.paymentQuote && !this.paymentQuoteError;
  }

  submit() {
    this.errorMessage = '';
    if (!this.hasDocument) {
      this.errorMessage = this.isStandalone
        ? 'Suba un documento PDF para firmar.'
        : 'Seleccione un PDF del requerimiento o cargue uno nuevo.';
      return;
    }
    if (!this.signingparties.length) {
      this.errorMessage = 'Agregue al menos un firmante.';
      return;
    }
    if (this.placementMode === 'custom' && !this.allSignersPlaced) {
      this.errorMessage = 'Posicione la firma de cada firmante antes de enviar.';
      return;
    }

    this.loading = true;

    const proceedCreate = () => {
      if (this.isStandalone) {
        if (!this.uploadedFile) {
          this.loading = false;
          this.errorMessage = 'Suba un documento PDF para firmar.';
          return;
        }
        this.createEnvelopeStandalone(this.uploadedFile);
        return;
      }

      if (this.uploadedFile) {
        const stamp = Date.now().toString();
        const baseName = this.uploadedFile.name.replace(/\.pdf$/i, '');
        const file = new File([this.uploadedFile], `${baseName}${stamp}.pdf`, {
          type: 'application/pdf'
        });
        const formData = this.getFormData({
          requirementid: this.requirementId,
          note: '',
          file
        });

        this.requirements.uploadOneFile(formData, 'trackvision').subscribe(
          (resp: any) => {
            if (resp?.error || !resp?.result?.id) {
              this.loading = false;
              this.errorMessage =
                resp?.error?.message || 'Error al subir el PDF al requerimiento.';
              return;
            }
            this.createEnvelope(
              resp.result.id,
              this.uploadedFile?.name || resp.result.filename || 'documento.pdf'
            );
          },
          (error) => {
            this.loading = false;
            this.errorMessage =
              error?.error?.message || error?.message || 'Error al subir el PDF.';
          }
        );
        return;
      }

      const attachment = this.selectedAttachment;
      this.createEnvelope(
        this.selectedAttachmentId as number,
        attachment?.filename || attachment?.name || 'documento.pdf'
      );
    };

    if (this.isAdvancedSignature && this.paymentMethod === 'card') {
      // NO se vuelve a cobrar si el cargo de este modal ya está confirmado.
      //
      // Antes, si la creación del envelope fallaba DESPUÉS del cargo, reintentar
      // ejecutaba `processCardPayment()` otra vez: segundo cobro, y sin decirle al
      // usuario que el primero había pasado. El backend además rechaza reusar el
      // mismo intent, así que reintentar con el pago hecho tiene que seguir
      // adelante, no volver a pasar la tarjeta.
      if (this.paymentConfirmed && this.paymentIntentId) {
        proceedCreate();
        return;
      }
      this.processCardPayment().then((ok) => {
        if (ok) {
          this.paymentConfirmed = true;
          proceedCreate();
        }
      });
      return;
    }

    proceedCreate();
  }
}
